import type { Activity, RolNivel } from '@/types'
import type { ArtistViewMode } from '@/hooks/useArtistViewMode'

// An activity is "mine" if it's public (everyone on the tour sees it) or
// if the current user is in its activity_assignments list.
function isActivityMine(
  activity: Activity,
  userId: string | undefined,
  assignments: Record<string, string[]> | undefined
): boolean {
  if (activity.visibility_level === 'public') return true
  if (!userId || !assignments) return false
  return assignments[activity.id]?.includes(userId) ?? false
}

interface ArtistViewResult {
  visible: Activity[]
  dimmedIds: Set<string>
}

// Filters / marks activities based on the viewer's role and their artist
// toggle state. Only Artists (B) get the toggle — Managers see everything,
// Crew rely on the RLS-filtered server response.
export function computeArtistView(
  activities: Activity[],
  role: RolNivel | undefined,
  mode: ArtistViewMode,
  assignments: Record<string, string[]> | undefined,
  userId: string | undefined
): ArtistViewResult {
  if (role !== 'B') {
    return { visible: activities, dimmedIds: new Set() }
  }

  if (mode === 'mine') {
    return {
      visible: activities.filter(a => isActivityMine(a, userId, assignments)),
      dimmedIds: new Set(),
    }
  }

  // mode === 'all' — keep everything, dim the ones that aren't mine so the
  // artist can visually separate "my schedule" from "informational".
  const dimmed = new Set<string>()
  for (const a of activities) {
    if (!isActivityMine(a, userId, assignments)) dimmed.add(a.id)
  }
  return { visible: activities, dimmedIds: dimmed }
}
